import { redirectToAuth, isSessionErrorBody } from './http';

const AUTH_CREDENTIALS = { credentials: 'include' };


function formatAuthApiError(err) {
  if (typeof err === 'string') return err;
  if (typeof err === 'number' || typeof err === 'boolean') return String(err);
  if (err && typeof err === 'object') {
    if (typeof err.message === 'string' && err.message.trim()) return err.message;
    if (typeof err.description === 'string' && err.description.trim()) return err.description;
    try {
      return JSON.stringify(err);
    } catch {
      return 'Ошибка авторизации';
    }
  }
  return 'Ошибка авторизации';
}

function scalarToIdString(v) {
  if (typeof v === 'string' && v.trim()) return v.trim();
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
}

function normalizeProfile(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = scalarToIdString(raw.id);
  const teacher_id = scalarToIdString(raw.teacher_id);
  const fio = raw.fio;
  const roles = raw.roles;
  if (!id || typeof fio !== 'string' || !Array.isArray(roles)) return null;
  if (!roles.every((r) => typeof r === 'string' || typeof r === 'number')) return null;
  return {
    id,
    fio,
    roles: roles.map(String),
    teacher_id,
  };
}

function parseWheelAccess(raw) {
  if (!raw || typeof raw !== 'object') return undefined;
  if (typeof raw.has_wheel_access !== 'boolean') return undefined;
  return {
    hasWheelAccess: raw.has_wheel_access,
    isStaff: Boolean(raw.is_staff),
    isTeacherOnly: Boolean(raw.is_teacher_only),
  };
}

async function parseAuthResponse(response) {
  const contentType = response.headers.get('content-type') ?? '';
  let body = null;
  if (contentType.toLowerCase().includes('application/json')) {
    try {
      body = await response.json();
    } catch {
      body = null;
    }
  } else if (response.ok) {
    try {
      body = JSON.parse(await response.text());
    } catch {
      body = null;
    }
  }

  if (response.status === 401 || response.status === 404 || response.status === 403) {
    const b = body && typeof body === 'object' ? body : null;
    if (response.status === 401 && isSessionErrorBody(b)) {
      return {
        status: 'unauthorized',
        error: b.error,
        message:
          typeof b.description === 'string' && b.description.trim()
            ? b.description.trim()
            : 'Нет сессии. Войдите снова.',
        redirectToAuth: true,
      };
    }
    if (response.status === 403 && b?.error === 'no_wheel_access') {
      const msg =
        typeof b.description === 'string' && b.description.trim()
          ? b.description.trim()
          : 'Нет доступа к колесу баланса';
      return { status: 'forbidden', message: msg };
    }
    if (response.status === 403 && b?.error === 'missing_teacher_id') {
      const msg =
        typeof b.description === 'string' && b.description.trim()
          ? b.description.trim()
          : 'У учётной записи педагога не указан идентификатор (teacher_id)';
      return { status: 'forbidden', message: msg };
    }
    return { status: 'unauthorized', redirectToAuth: response.status === 401 };
  }

  if (response.status === 429 && body && typeof body === 'object') {
    const desc =
      typeof body.description === 'string' && body.description.trim()
        ? body.description.trim()
        : 'Слишком много попыток. Подождите минуту.';
    return { status: 'client_error', message: desc };
  }

  // Topaz often answers with HTTP 202 + success:false for bad credentials.
  if (response.status === 202 && body && typeof body === 'object' && body.success === false) {
    return {
      status: 'client_error',
      message: formatAuthApiError(body.error || body.description),
    };
  }

  if (!response.ok) {
    const b = body && typeof body === 'object' ? body : null;
    const desc =
      typeof b?.description === 'string' && b.description.trim() ? b.description.trim() : null;
    const err = typeof b?.error === 'string' && b.error.trim() ? b.error.trim() : null;
    const msg = desc ?? err ?? `HTTP ${response.status}`;
    return { status: 'system_error', message: msg };
  }

  if (body?.success === true) {
    const data = normalizeProfile(body.data);
    if (data) {
      const access = parseWheelAccess(body.wheel_access);
      return { status: 'ok', data: access ? { ...data, access } : data };
    }
  }

  if (body && typeof body === 'object' && body.success === false) {
    return {
      status: 'client_error',
      message: formatAuthApiError(body.error || body.description),
    };
  }

  return { status: 'unauthorized' };
}

export async function fetchAuthSession() {
  let response;
  try {
    response = await fetch('/api/wheel-auth', AUTH_CREDENTIALS);
  } catch (e) {
    return {
      status: 'system_error',
      message: e instanceof Error ? e.message : 'Ошибка сети',
    };
  }
  return parseAuthResponse(response);
}

export async function postAuthSession(login, password) {
  let response;
  try {
    response = await fetch('/api/auth/session', {
      ...AUTH_CREDENTIALS,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login, password }),
    });
  } catch (e) {
    return {
      status: 'system_error',
      message: e instanceof Error ? e.message : 'Ошибка сети',
    };
  }
  return parseAuthResponse(response);
}

export async function postAuthLogout() {
  try {
    await fetch('/api/auth/logout', { ...AUTH_CREDENTIALS, method: 'POST' });
  } catch {
    /* ignore */
  }
}

export async function postAuthResetAttempts() {
  let response;
  try {
    response = await fetch('/api/auth/session/reset-attempts', {
      ...AUTH_CREDENTIALS,
      method: 'POST',
    });
  } catch (e) {
    return {
      ok: false,
      message: e instanceof Error ? e.message : 'Ошибка сети',
    };
  }
  if (!response.ok) {
    let message = 'Не удалось сбросить попытки';
    try {
      const body = await response.json();
      if (typeof body.description === 'string' && body.description.trim()) {
        message = body.description.trim();
      }
    } catch {
      /* ignore */
    }
    return { ok: false, message };
  }
  return { ok: true };
}

export { formatAuthApiError };
